import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';
import { LOCALES_DEBUG } from '@lib/enums/localStorageKeys';
import resources from './resources';

const { VITE_ENVIRONMENT } = import.meta.env;

// Check if debug mode is enabled
const isDebugMode = () => {
  if (typeof window === 'undefined' || !('localStorage' in window))
    return false;

  try {
    const debug = window.localStorage.getItem(LOCALES_DEBUG);
    return debug ? JSON.parse(debug) : false;
  } catch (error) {
    console.warn('✋', error);
    // Access to localStorage may throw in opaque origins (e.g., headless converters)
    return false;
  }
};

// Detect the browser's preferred language and map it to supported locales
const supportedLanguagesKeys = Object.keys(resources);
const supportedLanguages =
  VITE_ENVIRONMENT === 'DEV' ? supportedLanguagesKeys : ['en', 'fr'];
const fallbackLanguage = 'en';

function detectBrowserLanguage(): string {
  // Guard for non-browser environments (e.g., SSR)
  if (typeof navigator === 'undefined') return fallbackLanguage;

  const nav: any = navigator as any;
  let langs: string[] = [];
  if (Array.isArray(nav.languages) && nav.languages.length) {
    langs = nav.languages as string[];
  } else if (nav.language) {
    langs = [String(nav.language)];
  }

  // Normalize: try full tag (e.g., "en-US") then base ("en")
  const candidates = langs
    .filter(Boolean)
    .map((l) => String(l).toLowerCase())
    .flatMap((l) => [l, l.split('-')[0]]);

  const match = candidates.find((l) => supportedLanguages.includes(l));
  return match || fallbackLanguage;
}

i18n
  .use(initReactI18next)
  .use(ICU)
  .init({
    resources,
    lng: detectBrowserLanguage(),
    fallbackLng: fallbackLanguage,
    interpolation: {
      escapeValue: false,
    },
    ...(isDebugMode() && {
      // In debug mode, return the key instead of the translation
      parseMissingKeyHandler: (key: string) => key,
      returnEmptyString: false,
      returnNull: false,
      // Override the translation function to return keys
      missingKeyHandler: () => undefined,
    }),
  });

// Override the t function in debug mode
if (isDebugMode()) {
  const originalT = i18n.t.bind(i18n);
  i18n.t = function debugT(key: any, options?: any) {
    if (typeof key === 'string') {
      return key;
    }
    return originalT(key, options);
  } as typeof i18n.t;
}

export default i18n;
